/**
 * Transport Adapter — translates Canvas Relay binary messages into
 * Google A2UI v0.8 ServerToClientMessage[].
 *
 * This is the bridge between BeeOS's Canvas Relay transport layer and
 * Google's @a2ui/react message processor.
 */

import type { Types } from "@a2ui/react";
import type { SurfacePresentation, ACPCanvasUpdateEnvelope } from "./types";

type ProcessMessagesFn = (messages: Types.ServerToClientMessage[]) => void;
type ValueMap = Types.DataModelUpdate extends { contents: infer C } ? (C extends (infer V)[] ? V : never) : never;

// Binary protocol constants (same as Canvas Relay)
const MSG_SYNC = 0x00;
const MSG_SYNC_UPDATE = 0x02;

function toValueMapArray(data: Record<string, unknown>): ValueMap[] {
  return Object.entries(data).map(([key, value]): ValueMap => {
    if (typeof value === "string") return { key, valueString: value } as ValueMap;
    if (typeof value === "number") return { key, valueNumber: value } as ValueMap;
    if (typeof value === "boolean") return { key, valueBoolean: value } as ValueMap;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return { key, valueMap: toValueMapArray(value as Record<string, unknown>) } as ValueMap;
    }
    return { key, valueString: String(value ?? "") } as ValueMap;
  });
}

function normalizeLegacyTextContent(
  components: Types.ComponentInstance[],
): Types.ComponentInstance[] {
  return components.map((component) => {
    const record = component as unknown as {
      readonly component?: Record<string, unknown>;
    };
    const text = record.component?.Text;
    if (typeof text !== "object" || text === null) return component;

    const properties = text as Record<string, unknown>;
    if (properties.text !== undefined || properties.content === undefined) return component;

    const content = properties.content;
    const normalizedText = typeof content === "object" && content !== null
      ? content
      : { literalString: String(content) };
    const { content: _legacyContent, ...remainingProperties } = properties;
    return {
      ...component,
      component: {
        ...record.component,
        Text: {
          ...remainingProperties,
          text: normalizedText,
        },
      },
    } as Types.ComponentInstance;
  });
}

export interface TransportAdapterDeps {
  processMessages: ProcessMessagesFn;
  onPresentationChange?: (surfaceId: string, presentation: SurfacePresentation) => void;
}

/**
 * Process a Canvas Relay binary sync message.
 *
 * Relay sends full surface state as:
 *   [0x00(MsgSync), 0x02(MsgSyncUpdate), ...JSON]
 *
 * JSON payload: { surfaceId, components, presentation?, deleted? }
 * where `components` is a Google v0.8 ComponentInstance[].
 *
 * We convert this to beginRendering + surfaceUpdate messages.
 */
/**
 * Process an ACP `canvas.update` envelope.
 */
export function processACPMessage(
  deps: TransportAdapterDeps,
  envelope: ACPCanvasUpdateEnvelope,
): void {
  if (envelope.presentation) {
    deps.onPresentationChange?.(envelope.surfaceId, envelope.presentation);
  }
  deps.processMessages([envelope.message]);
}

/**
 * Find the root component ID from a flat component list by identifying the
 * component not referenced as a child by any other component. This mirrors
 * the agent-side `findRootIds` logic in beeos-claw/src/a2ui-helpers.ts.
 */
function inferRootId(components: Types.ComponentInstance[]): string | undefined {
  const referenced = new Set<string>();
  for (const node of components) {
    const comp = node.component as Record<string, Record<string, unknown>> | undefined;
    if (!comp) continue;
    const inner = Object.values(comp)[0];
    if (!inner) continue;
    if (typeof inner.child === "string") referenced.add(inner.child);
    const children = inner.children as { explicitList?: string[] } | undefined;
    if (children?.explicitList) {
      for (const cid of children.explicitList) referenced.add(cid);
    }
    const tabItems = inner.tabItems as Array<{ child?: string; childId?: string }> | undefined;
    if (tabItems) {
      for (const t of tabItems) {
        if (t.child) referenced.add(t.child);
        if (t.childId) referenced.add(t.childId);
      }
    }
  }
  const roots = components.filter((n) => !referenced.has(n.id));
  return roots.length > 0 ? roots[roots.length - 1].id : undefined;
}

export function processRelayMessage(
  deps: TransportAdapterDeps,
  data: Uint8Array,
): boolean {
  if (data.length < 3) return false;
  if (data[0] !== MSG_SYNC || data[1] !== MSG_SYNC_UPDATE) return false;

  try {
    const json = new TextDecoder().decode(data.subarray(2));
    const payload = JSON.parse(json) as {
      surfaceId?: string;
      components?: Types.ComponentInstance[];
      presentation?: SurfacePresentation;
      deleted?: boolean;
      root?: string;
      dataModel?: Record<string, unknown>;
    };

    if (!payload.surfaceId) return false;

    if (payload.deleted) {
      deps.processMessages([{ deleteSurface: { surfaceId: payload.surfaceId } }]);
      return true;
    }

    if (Array.isArray(payload.components) && payload.components.length > 0) {
      const components = normalizeLegacyTextContent(payload.components);
      if (payload.presentation) {
        deps.onPresentationChange?.(payload.surfaceId, payload.presentation);
      }

      const rootId = payload.root || inferRootId(components) || components[0]?.id;
      if (!rootId) return false;

      const messages: Types.ServerToClientMessage[] = [
        { beginRendering: { surfaceId: payload.surfaceId, root: rootId } },
      ];

      if (payload.dataModel && typeof payload.dataModel === "object") {
        messages.push({
          dataModelUpdate: {
            surfaceId: payload.surfaceId,
            path: "/",
            contents: toValueMapArray(payload.dataModel as Record<string, unknown>),
          },
        });
      }

      messages.push(
        { surfaceUpdate: { surfaceId: payload.surfaceId, components } },
      );

      deps.processMessages(messages);
      return true;
    }

    return false;
  } catch {
    return false;
  }
}
